import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import Breadcrumbs from "../components/common/Breadcrumbs";
import Modal from "../components/common/Modal";
import PageMeta from "../components/common/PageMeta";
import ProductGrid from "../components/product/ProductGrid";
import CollectionToolbar from "../components/shop/CollectionToolbar";
import FilterPanel from "../components/shop/FilterPanel";
import products, { productCategories } from "../data/products";

const normalize = (value) => String(value || "").toLowerCase().replace(/&/g, "and").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const collectionCopy = {
  "ground-spices": ["Ground Spices", "Vivid color, clean aroma, and enough character to change the whole pot."],
  "whole-spices": ["Whole Spices", "Toast, crack, bloom—and build flavor from the beginning."],
  herbs: ["Herbs", "Leafy, fragrant essentials for sauces, soups, roasts, and salads."],
  blends: ["Seasoning Blends", "Balanced in small batches so dinner starts a few steps ahead."],
  baking: ["Baking", "Warm aromatics and pantry staples for sweet and savory baking."],
  salts: ["Gourmet Salts", "Clean mineral crunch, delicate flakes, and deep natural smoke."],
  "rare-ingredients": ["Rare Ingredients", "Hard-to-find flavors with practical ideas for putting them to work."],
  "spices-seasonings-herbs": ["Spices, Seasonings & Herbs", "Ground and whole spices, culinary herbs, and small-batch blends for every shelf."],
};

const categoryGroups = {
  "spices-seasonings-herbs": ["ground-spices", "whole-spices", "herbs", "blends"],
};

const getRouteCategories = (category) => {
  const normalized = normalize(category);
  if (!normalized) return [];
  return categoryGroups[normalized] || [normalized];
};

const namedCollections = {
  "best-sellers": ["Best Sellers", "The vivid, versatile jars customers return to most often."],
  "seasonings-herbs": ["Seasonings & Herbs", "Small-batch blends and fragrant culinary herbs for everyday cooking."],
  "salt-specialties": ["Salts & Specialties", "Mineral crunch, delicate flakes, and rare finishing ingredients."],
};

const catalogMaxPrice = Math.max(...products.map((product) => product.price));

const ShopPage = ({ mode = "shop" }) => {
  const { category: routeCategory } = useParams();
  const location = useLocation();
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const urlCategory = routeCategory || queryParams.get("category") || "";
  const searchQuery = mode === "search" ? queryParams.get("q") || "" : "";
  const collection = queryParams.get("collection") || "";
  const requestedTag = normalize(queryParams.get("tag") || "");
  const [selectedCategories, setSelectedCategories] = useState(() => getRouteCategories(urlCategory));
  const [selectedTags, setSelectedTags] = useState(requestedTag ? [requestedTag] : []);
  const [maxPrice, setMaxPrice] = useState(catalogMaxPrice);
  const [sort, setSort] = useState(collection === "best-sellers" ? "best-selling" : "featured");
  const [view, setView] = useState("grid");
  const [mobileFilters, setMobileFilters] = useState(false);

  useEffect(() => {
    setSelectedCategories(getRouteCategories(urlCategory));
  }, [urlCategory]);

  useEffect(() => {
    setSelectedTags(requestedTag ? [requestedTag] : []);
  }, [requestedTag]);

  const popularTags = useMemo(() => {
    const counts = new Map();
    products.flatMap((product) => product.tags).forEach((tag) => counts.set(tag, (counts.get(tag) || 0) + 1));
    return [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 10).map(([tag]) => tag);
  }, []);

  const filteredProducts = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const filtered = products.filter((product) => {
      const categoryMatch = !selectedCategories.length || selectedCategories.includes(normalize(product.category));
      const productFacets = [
        ...product.tags,
        ...product.badges,
        ...(product.attributes.dietary || []),
      ].map(normalize);
      const tagMatch = !selectedTags.length || selectedTags.every((tag) => productFacets.includes(normalize(tag)));
      const priceMatch = product.price <= maxPrice;
      const searchable = [product.name, product.category, product.subcategory, product.shortDescription, ...product.tags].join(" ").toLowerCase();
      const searchMatch = !query || searchable.includes(query);
      const collectionMatch =
        !collection ||
        (collection === "best-sellers" && product.badges.includes("Best Seller")) ||
        (collection === "seasonings-herbs" && ["Blends", "Herbs"].includes(product.category)) ||
        (collection === "salt-specialties" && ["Salts", "Rare Ingredients"].includes(product.category));
      return categoryMatch && tagMatch && priceMatch && searchMatch && collectionMatch;
    });

    return [...filtered].sort((a, b) => {
      if (sort === "best-selling") return b.reviewCount - a.reviewCount;
      if (sort === "rating") return b.rating - a.rating;
      if (sort === "price-asc") return a.price - b.price;
      if (sort === "price-desc") return b.price - a.price;
      if (sort === "name-asc") return a.name.localeCompare(b.name);
      return Number(b.badges.includes("Best Seller")) - Number(a.badges.includes("Best Seller"));
    });
  }, [collection, maxPrice, searchQuery, selectedCategories, selectedTags, sort]);

  const toggleItem = (setter) => (value) => setter((current) => current.includes(value) ? current.filter((item) => item !== value) : [...current, value]);
  const clearFilters = () => { setSelectedCategories(getRouteCategories(urlCategory)); setSelectedTags(requestedTag ? [requestedTag] : []); setMaxPrice(catalogMaxPrice); };
  const selectedCopy = collectionCopy[normalize(urlCategory)] || collectionCopy[selectedCategories[0]];
  const namedCollection = namedCollections[collection];
  const title = mode === "search"
    ? searchQuery ? `Search results for “${searchQuery}”` : "Search the pantry"
    : namedCollection?.[0] || selectedCopy?.[0] || "All Spices & Pantry Ingredients";
  const intro = mode === "search"
    ? `${filteredProducts.length} result${filteredProducts.length === 1 ? "" : "s"} across our pantry.`
    : namedCollection?.[1] || selectedCopy?.[1] || "Shop our edited collection of small-batch spices, herbs, salts, baking essentials, and rare ingredients.";

  const filterProps = {
    categories: productCategories,
    selectedCategories,
    onCategory: toggleItem(setSelectedCategories),
    price: maxPrice,
    onPrice: setMaxPrice,
    maxAvailablePrice: catalogMaxPrice,
    tags: popularTags,
    selectedTags,
    onTag: toggleItem(setSelectedTags),
    onClear: clearFilters,
  };

  return (
    <div className="collection-page">
      <PageMeta title={title} description={intro} />
      <div className="collection-hero">
        <div className="site-container">
          <Breadcrumbs items={[{ label: "Home", href: "/" }, { label: mode === "search" ? "Search" : "Shop" }]} />
          <p className="eyebrow">The pantry index</p>
          <h1>{title}</h1>
          <p>{intro}</p>
        </div>
      </div>
      <div className="site-container collection-page__body">
        <CollectionToolbar count={filteredProducts.length} sort={sort} onSort={setSort} view={view} onView={setView} onFilter={() => setMobileFilters(true)} />
        <div className="collection-page__layout">
          <FilterPanel {...filterProps} />
          <ProductGrid
            products={filteredProducts}
            view={view}
            columns={4}
            emptyTitle={searchQuery ? `No results for “${searchQuery}”` : "No products match these filters"}
            emptyDescription="Try a broader category, a different flavor, or clear the filters."
            emptyActionLabel={searchQuery ? "Browse all products" : "Clear filters"}
            emptyActionTo={searchQuery ? "/shop" : undefined}
            onEmptyAction={searchQuery ? undefined : clearFilters}
            cardProps={{ showDescription: view === "list" }}
          />
        </div>
      </div>
      <Modal open={mobileFilters} onClose={() => setMobileFilters(false)} title="Filter pantry" size="small">
        <FilterPanel {...filterProps} mobile onApply={() => setMobileFilters(false)} />
      </Modal>
    </div>
  );
};

export default ShopPage;
